'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { PageAlertConfig } from '../../types/page-alerts.ts';

type PageAlertOwner = symbol;

type PageAlertRegistration = {
  config: PageAlertConfig;
  owner: PageAlertOwner;
  order: number;
};

type PageAlertActions = {
  registerAlert: (owner: PageAlertOwner, config: PageAlertConfig) => void;
  unregisterAlert: (owner: PageAlertOwner, id: string) => void;
  removeAlert: (id: string) => void;
};

const PageAlertActionsContext = createContext<PageAlertActions | null>(null);
const PageAlertStateContext = createContext<PageAlertConfig[] | null>(null);

const arePageAlertConfigsEqual = (
  first: PageAlertConfig,
  second: PageAlertConfig,
) => {
  const keys = Object.keys(first) as Array<keyof PageAlertConfig>;
  return (
    keys.length === Object.keys(second).length &&
    keys.every((key) => Object.is(first[key], second[key]))
  );
};

export const PageAlertProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [registrations, setRegistrations] = useState<
    Map<string, PageAlertRegistration>
  >(() => new Map());
  const nextOrder = useRef(0);

  const registerAlert = useCallback(
    (owner: PageAlertOwner, config: PageAlertConfig) => {
      setRegistrations((current) => {
        const existing = current.get(config.id);
        if (
          existing?.owner === owner &&
          arePageAlertConfigsEqual(existing.config, config)
        ) {
          return current;
        }

        const next = new Map(current);
        next.set(config.id, {
          config,
          owner,
          order: existing?.order ?? nextOrder.current++,
        });
        return next;
      });
    },
    [],
  );

  const unregisterAlert = useCallback((owner: PageAlertOwner, id: string) => {
    setRegistrations((current) => {
      const existing = current.get(id);
      if (!existing || existing.owner !== owner) {
        return current;
      }

      const next = new Map(current);
      next.delete(id);
      return next;
    });
  }, []);

  const removeAlert = useCallback((id: string) => {
    setRegistrations((current) => {
      if (!current.has(id)) {
        return current;
      }

      const next = new Map(current);
      next.delete(id);
      return next;
    });
  }, []);

  const actions = useMemo(
    () => ({ registerAlert, unregisterAlert, removeAlert }),
    [registerAlert, unregisterAlert, removeAlert],
  );
  const alerts = useMemo(
    () =>
      [...registrations.values()]
        .sort(
          (a, b) =>
            (b.config.priority ?? 0) - (a.config.priority ?? 0) ||
            a.order - b.order,
        )
        .map(({ config }) => config),
    [registrations],
  );

  return (
    <PageAlertActionsContext.Provider value={actions}>
      <PageAlertStateContext.Provider value={alerts}>
        {children}
      </PageAlertStateContext.Provider>
    </PageAlertActionsContext.Provider>
  );
};

export const usePageAlert = (config?: PageAlertConfig) => {
  const actions = useContext(PageAlertActionsContext);
  if (!actions) {
    throw new Error('usePageAlert must be used within <PageAlertProvider />');
  }

  const owner = useRef<PageAlertOwner>(Symbol('page-alert'));

  useEffect(() => {
    if (!config) {
      return;
    }

    actions.registerAlert(owner.current, config);
    return () => actions.unregisterAlert(owner.current, config.id);
  }, [actions, config]);
};

export const usePageAlerts = () => {
  const alerts = useContext(PageAlertStateContext);
  if (!alerts) {
    throw new Error('usePageAlerts must be used within <PageAlertProvider />');
  }

  return alerts;
};

export const useRemovePageAlert = () => {
  const actions = useContext(PageAlertActionsContext);
  if (!actions) {
    throw new Error(
      'useRemovePageAlert must be used within <PageAlertProvider />',
    );
  }

  return actions.removeAlert;
};
